import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { apiariesApi, type Apiary } from '../services/apiaries';
import { BroodRoomIcon } from '../components/hives/BroodRoomIcon';
import { QueenColorIcon } from '../components/hives/QueenColorIcon';
import { hivesApi, type Hive, type HiveLog, type HiveMetaItem, type MedicineLogRow } from '../services/hives';

type Tab = 'hives' | 'logs' | 'medicine';

type HiveForm = {
  name: string;
  apiarySiteId: string;
  hiveNo: string;
  hiveCode: string;
  hiveType: string;
  hiveFormatItemId: string;
  broodRoomTypeItemId: string;
  hiveStatusItemId: string;
  queenColor: string;
  queenStatusItemId: string;
  startedAt: string;
  endedAt: string;
  notes: string;
  siteNotes: string;
};

const emptyHiveForm: HiveForm = {
  name: '',
  apiarySiteId: '',
  hiveNo: '',
  hiveCode: '',
  hiveType: '',
  hiveFormatItemId: '',
  broodRoomTypeItemId: '',
  hiveStatusItemId: '',
  queenColor: '',
  queenStatusItemId: '',
  startedAt: '',
  endedAt: '',
  notes: '',
  siteNotes: '',
};

type LogForm = {
  logDate: string;
  eventType: string;
  isTreatment: boolean;
  preparationProductId: string;
  preparation: string;
  withdrawalDays: string;
  notes: string;
  createSplitHive: boolean;
  queenColor: string;
};

const emptyLogForm: LogForm = {
  logDate: '',
  eventType: '',
  isTreatment: false,
  preparationProductId: '',
  preparation: '',
  withdrawalDays: '',
  notes: '',
  createSplitHive: false,
  queenColor: '',
};

function todayDateValue() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function defaultBulkLogForm(): LogForm {
  return {
    ...emptyLogForm,
    logDate: todayDateValue(),
    eventType: 'Allmän kommentar',
  };
}

const QUEEN_COLOR_LOG_TYPES = new Set([
  'Isättning av parad drottning',
  'Isättning av oparad drottning',
  'Isättning av drottningcell',
  'Egenuppdragen, parad, drottning noterad',
]);

const TREATMENT_LOG_TYPES = new Set(['Varroabehandling']);
const PREPARATION_REMOVED_LOG_TYPES = new Set(['Preparat borttaget']);

const TRIGGER_CREATE_HIVE_LOG_TYPES = new Set([
  'Gör avläggare (walk-away)',
  'Gör konstgjord svärm',
  'Ny kupa - fångad svärm',
  'Ny kupa - köpt',
]);

function toLocalDateTime(dt?: string | null): string {
  if (!dt) return '-';
  const d = new Date(dt.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return dt;
  return d.toLocaleString('sv-SE');
}

function isInQuarantine(withdrawalUntil?: string | null): boolean {
  if (!withdrawalUntil) return false;
  const until = new Date(withdrawalUntil.replace(' ', 'T'));
  if (Number.isNaN(until.getTime())) return false;
  return until.getTime() >= Date.now();
}

function QuarantineIcon({ active }: { active: boolean }) {
  return (
    <span
      aria-label={active ? 'I karantän' : 'Ingen karantän'}
      title={active ? 'I karantän' : 'Ingen karantän'}
      className={`inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px] font-semibold ${
        active ? 'border-danger bg-danger text-white' : 'border-gray-300 bg-white text-gray-400'
      }`}
    >
      !
    </span>
  );
}

function splitStatusLabel(label: string | null | undefined): { short: string; long: string } {
  const raw = (label || '').trim();
  if (!raw) return { short: '-', long: '-' };
  const idx = raw.indexOf(' - ');
  if (idx > 0) {
    const short = raw.slice(0, idx).trim();
    const long = raw.slice(idx + 3).trim();
    return { short, long: long || short };
  }
  return { short: raw, long: raw };
}

function requiresQueenColor(eventType: string): boolean {
  return QUEEN_COLOR_LOG_TYPES.has(eventType);
}

function isTreatmentLogType(eventType: string): boolean {
  return TREATMENT_LOG_TYPES.has(eventType);
}

function isPreparationRemovedLogType(eventType: string): boolean {
  return PREPARATION_REMOVED_LOG_TYPES.has(eventType);
}

function createsHiveFromLogType(eventType: string): boolean {
  return TRIGGER_CREATE_HIVE_LOG_TYPES.has(eventType);
}

function filterButton(active: boolean): string {
  return active
    ? 'rounded border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white'
    : 'rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50';
}

export function HivesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { canWrite } = useAuth();
  const [tab, setTab] = useState<Tab>('hives');
  const [loading, setLoading] = useState(true);
  const [hives, setHives] = useState<Hive[]>([]);
  const [apiaries, setApiaries] = useState<Apiary[]>([]);
  const [selectedHiveId, setSelectedHiveId] = useState<number | null>(null);
  const [logs, setLogs] = useState<HiveLog[]>([]);
  const [medicineRows, setMedicineRows] = useState<MedicineLogRow[]>([]);

  const [q, setQ] = useState('');
  const [apiaryFilter, setApiaryFilter] = useState('');
  const [queenStatusFilters, setQueenStatusFilters] = useState<Set<string>>(() => new Set());
  const [hiveStatusFilters, setHiveStatusFilters] = useState<Set<string>>(() => new Set());
  const [broodRoomFilters, setBroodRoomFilters] = useState<Set<string>>(() => new Set());
  const [includeInactive, setIncludeInactive] = useState(false);

  const [showHiveForm, setShowHiveForm] = useState(false);
  const [editingHive, setEditingHive] = useState<Hive | null>(null);
  const [hiveForm, setHiveForm] = useState<HiveForm>(emptyHiveForm);
  const [savingHive, setSavingHive] = useState(false);
  const [loadingNextHiveNo, setLoadingNextHiveNo] = useState(false);

  const [logForm, setLogForm] = useState<LogForm>(emptyLogForm);
  const [savingLog, setSavingLog] = useState(false);
  const [selectedHiveIds, setSelectedHiveIds] = useState<Set<number>>(() => new Set());
  const [bulkLogForm, setBulkLogForm] = useState<LogForm>(defaultBulkLogForm);
  const [savingBulkLog, setSavingBulkLog] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState<number | null>(null);
  const [pendingDeleteLog, setPendingDeleteLog] = useState<HiveLog | null>(null);

  const [medicineFrom, setMedicineFrom] = useState('');
  const [medicineTo, setMedicineTo] = useState('');
  const [medicineApiaryId, setMedicineApiaryId] = useState('');
  const [hiveStatusItems, setHiveStatusItems] = useState<HiveMetaItem[]>([]);
  const [queenStatusItems, setQueenStatusItems] = useState<HiveMetaItem[]>([]);
  const [hiveFormatItems, setHiveFormatItems] = useState<HiveMetaItem[]>([]);
  const [broodRoomItems, setBroodRoomItems] = useState<HiveMetaItem[]>([]);
  const [hiveLogTypeItems, setHiveLogTypeItems] = useState<HiveMetaItem[]>([]);
  const [preparations, setPreparations] = useState<Array<{ id: number; sku: string | null; name: string; withdrawalDays: number | null; treatmentLengthDays: number | null; instructionUrl: string | null }>>([]);

  const selectedHive = useMemo(() => hives.find((h) => h.id === selectedHiveId) ?? null, [hives, selectedHiveId]);
  const selectedLogType = logForm.eventType.trim();
  const showTreatmentFields = logForm.isTreatment || isTreatmentLogType(selectedLogType);
  const showPreparationRemovedFields = isPreparationRemovedLogType(selectedLogType);
  const showCreateHiveFields = logForm.createSplitHive || createsHiveFromLogType(selectedLogType);
  const showQueenColorField = requiresQueenColor(selectedLogType);
  const selectedBulkLogType = bulkLogForm.eventType.trim();
  const showBulkTreatmentFields = bulkLogForm.isTreatment || isTreatmentLogType(selectedBulkLogType);
  const showBulkPreparationRemovedFields = isPreparationRemovedLogType(selectedBulkLogType);
  const showBulkQueenColorField = requiresQueenColor(selectedBulkLogType);
  const displayHives = useMemo(
    () =>
      hives.filter((hive) => {
        if (queenStatusFilters.size > 0 && !queenStatusFilters.has(String(hive.queenStatusItemId ?? ''))) return false;
        if (hiveStatusFilters.size > 0 && !hiveStatusFilters.has(String(hive.hiveStatusItemId ?? ''))) return false;
        if (broodRoomFilters.size > 0 && !broodRoomFilters.has(String(hive.broodRoomTypeItemId ?? ''))) return false;
        return true;
      }),
    [broodRoomFilters, hiveStatusFilters, hives, queenStatusFilters],
  );
  const selectedDisplayHiveIds = useMemo(
    () => displayHives.filter((hive) => selectedHiveIds.has(hive.id)).map((hive) => hive.id),
    [displayHives, selectedHiveIds],
  );
  const selectedHiveCount = selectedDisplayHiveIds.length;
  const allVisibleHivesSelected = displayHives.length > 0 && displayHives.every((hive) => selectedHiveIds.has(hive.id));

  const loadBase = useCallback(async () => {
    try {
      const [hivesRes, apiariesRes] = await Promise.all([
        hivesApi.list({
          q: q.trim() || undefined,
          apiaryId: apiaryFilter ? Number(apiaryFilter) : undefined,
          includeInactive,
        }),
        apiariesApi.list(),
      ]);
      const metaRes = await hivesApi.meta();
      setHives(hivesRes.hives);
      setApiaries(apiariesRes.apiaries);
      setHiveStatusItems(metaRes.lists.HIVE_STATUS ?? []);
      setQueenStatusItems(metaRes.lists.QUEEN_STATUS ?? []);
      setHiveFormatItems(metaRes.lists.HIVE_FORMAT ?? []);
      setBroodRoomItems(metaRes.lists.BROOD_ROOM_TYPES ?? []);
      setHiveLogTypeItems(metaRes.lists.HIVE_LOG_TYPES ?? []);
      setPreparations(metaRes.preparations ?? []);
      if (!selectedHiveId && hivesRes.hives.length > 0) {
        setSelectedHiveId(hivesRes.hives[0].id);
      }
      if (selectedHiveId && !hivesRes.hives.some((h) => h.id === selectedHiveId)) {
        setSelectedHiveId(hivesRes.hives[0]?.id ?? null);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda kupor');
    } finally {
      setLoading(false);
    }
  }, [q, apiaryFilter, includeInactive, selectedHiveId, toast]);

  const loadLogs = useCallback(async (hiveId: number) => {
    try {
      const res = await hivesApi.logs.list(hiveId);
      setLogs(res.logs);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda kuplogg');
    }
  }, [toast]);

  const loadMedicine = useCallback(async () => {
    try {
      const res = await hivesApi.medicineLog({
        from: medicineFrom || undefined,
        to: medicineTo || undefined,
        apiaryId: medicineApiaryId ? Number(medicineApiaryId) : undefined,
      });
      setMedicineRows(res.rows);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda läkemedelslogg');
    }
  }, [medicineFrom, medicineTo, medicineApiaryId, toast]);

  useEffect(() => {
    setLoading(true);
    loadBase();
  }, [loadBase]);

  useEffect(() => {
    if (selectedHiveId) {
      loadLogs(selectedHiveId);
    } else {
      setLogs([]);
    }
  }, [selectedHiveId, loadLogs]);

  useEffect(() => {
    if (tab === 'medicine') {
      loadMedicine();
    }
  }, [tab, loadMedicine]);

  useEffect(() => {
    const visibleIds = new Set(displayHives.map((hive) => hive.id));
    setSelectedHiveIds((current) => {
      const next = new Set(Array.from(current).filter((id) => visibleIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [displayHives]);

  const openCreateHive = async () => {
    setEditingHive(null);
    setHiveForm(emptyHiveForm);
    setShowHiveForm(true);
    setLoadingNextHiveNo(true);
    try {
      const res = await hivesApi.nextNumber();
      setHiveForm((f) => ({ ...f, hiveNo: String(res.nextHiveNo) }));
    } catch {
      // Keep form usable even if suggestion endpoint fails.
    } finally {
      setLoadingNextHiveNo(false);
    }
  };

  const saveHive = async () => {
    if (!hiveForm.name.trim()) {
      toast.error('Namn krävs');
      return;
    }
    if (!hiveForm.apiarySiteId) {
      toast.error('Välj bigård');
      return;
    }
    if (hiveForm.hiveNo !== '' && (!Number.isInteger(Number(hiveForm.hiveNo)) || Number(hiveForm.hiveNo) < 1)) {
      toast.error('Kupnummer måste vara ett heltal större än 0');
      return;
    }

    const payload = {
      name: hiveForm.name.trim(),
      apiarySiteId: Number(hiveForm.apiarySiteId),
      hiveNo: hiveForm.hiveNo === '' ? null : Number(hiveForm.hiveNo),
      hiveCode: hiveForm.hiveCode.trim() || undefined,
      hiveType: hiveForm.hiveType.trim() || undefined,
      hiveFormatItemId: hiveForm.hiveFormatItemId === '' ? null : Number(hiveForm.hiveFormatItemId),
      broodRoomTypeItemId: hiveForm.broodRoomTypeItemId === '' ? null : Number(hiveForm.broodRoomTypeItemId),
      hiveStatusItemId: hiveForm.hiveStatusItemId === '' ? null : Number(hiveForm.hiveStatusItemId),
      queenColor: hiveForm.queenColor.trim() || undefined,
      queenStatusItemId: hiveForm.queenStatusItemId === '' ? null : Number(hiveForm.queenStatusItemId),
      startedAt: hiveForm.startedAt || undefined,
      endedAt: hiveForm.endedAt || undefined,
      notes: hiveForm.notes.trim() || undefined,
      siteNotes: hiveForm.siteNotes.trim() || undefined,
    };

    setSavingHive(true);
    try {
      if (editingHive) {
        await hivesApi.update(editingHive.id, payload);
        toast.success('Kupa uppdaterad');
      } else {
        const created = await hivesApi.create(payload);
        setSelectedHiveId(created.id);
        toast.success('Kupa skapad');
      }
      setShowHiveForm(false);
      setEditingHive(null);
      await loadBase();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara kupa');
    } finally {
      setSavingHive(false);
    }
  };

  const saveLog = async () => {
    if (!selectedHiveId) {
      toast.error('Välj en kupa först');
      return;
    }
    if (!logForm.eventType.trim()) {
      toast.error('Loggtyp krävs');
      return;
    }

    setSavingLog(true);
    try {
      await hivesApi.logs.create(selectedHiveId, {
        logDate: logForm.logDate ? logForm.logDate.replace('T', ' ') + ':00' : undefined,
        eventType: logForm.eventType.trim(),
        isTreatment: showTreatmentFields,
        preparationProductId: (showTreatmentFields || showPreparationRemovedFields) && logForm.preparationProductId ? Number(logForm.preparationProductId) : undefined,
        preparation: showTreatmentFields || showPreparationRemovedFields ? logForm.preparation.trim() || undefined : undefined,
        withdrawalDays: showTreatmentFields && logForm.withdrawalDays ? Number(logForm.withdrawalDays) : undefined,
        notes: logForm.notes.trim() || undefined,
        createSplitHive: showCreateHiveFields,
        queenColor: showQueenColorField ? logForm.queenColor.trim() || undefined : undefined,
      });

      setLogForm(emptyLogForm);
      toast.success('Kuplogg sparad');
      await loadLogs(selectedHiveId);
      await loadBase();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara kuplogg');
    } finally {
      setSavingLog(false);
    }
  };

  const toggleHiveSelection = (hiveId: number, checked: boolean) => {
    setSelectedHiveIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(hiveId);
      } else {
        next.delete(hiveId);
      }
      return next;
    });
  };

  const toggleVisibleHives = (checked: boolean) => {
    setSelectedHiveIds((current) => {
      const next = new Set(current);
      displayHives.forEach((hive) => {
        if (checked) {
          next.add(hive.id);
        } else {
          next.delete(hive.id);
        }
      });
      return next;
    });
  };

  const toggleSetFilter = (setter: Dispatch<SetStateAction<Set<string>>>, value: string) => {
    setter((current) => {
      const next = new Set(current);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const saveBulkLog = async () => {
    if (selectedDisplayHiveIds.length === 0 || savingBulkLog) return;
    if (!bulkLogForm.eventType.trim()) {
      toast.error('Loggtyp krävs');
      return;
    }

    const payload = {
      logDate: bulkLogForm.logDate ? `${bulkLogForm.logDate} 12:00:00` : undefined,
      eventType: bulkLogForm.eventType.trim(),
      isTreatment: showBulkTreatmentFields,
      preparationProductId: (showBulkTreatmentFields || showBulkPreparationRemovedFields) && bulkLogForm.preparationProductId ? Number(bulkLogForm.preparationProductId) : undefined,
      preparation: showBulkTreatmentFields || showBulkPreparationRemovedFields ? bulkLogForm.preparation.trim() || undefined : undefined,
      withdrawalDays: showBulkTreatmentFields && bulkLogForm.withdrawalDays ? Number(bulkLogForm.withdrawalDays) : undefined,
      notes: bulkLogForm.notes.trim() || undefined,
      queenColor: showBulkQueenColorField ? bulkLogForm.queenColor.trim() || undefined : undefined,
    };

    setSavingBulkLog(true);
    let savedCount = 0;
    try {
      for (const hiveId of selectedDisplayHiveIds) {
        await hivesApi.logs.create(hiveId, payload);
        savedCount++;
      }
      toast.success(`Kuplogg sparad på ${savedCount} ${savedCount === 1 ? 'kupa' : 'kupor'}`);
      setSelectedHiveIds(new Set());
      setBulkLogForm(defaultBulkLogForm());
      await loadBase();
      if (selectedHiveId) {
        await loadLogs(selectedHiveId);
      }
    } catch (e) {
      toast.error(e instanceof Error ? `Sparade ${savedCount} av ${selectedHiveCount}: ${e.message}` : `Sparade ${savedCount} av ${selectedHiveCount}`);
    } finally {
      setSavingBulkLog(false);
    }
  };

  const deleteLog = async (logId: number) => {
    if (!selectedHiveId || deletingLogId !== null) return;
    setDeletingLogId(logId);
    try {
      await hivesApi.logs.remove(selectedHiveId, logId);
      setPendingDeleteLog(null);
      toast.success('Kuplogg raderad');
      await loadLogs(selectedHiveId);
      await loadBase();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte radera kuplogg');
    } finally {
      setDeletingLogId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-lg font-semibold">Kupor och Kuplogg</h1>
        <WithWrite moduleCode="hives">
          <button type="button" className="w-full rounded bg-success px-3 py-2 text-sm text-white hover:bg-success-hover sm:w-auto sm:px-2 sm:py-1" onClick={openCreateHive}>
            Skapa ny kupa
          </button>
        </WithWrite>
      </div>

      <nav className="flex gap-1 border-b border-gray-200">
        <button type="button" onClick={() => setTab('hives')} className={`px-2 py-1.5 text-sm rounded-t ${tab === 'hives' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}>
          Kupor
        </button>
        <button type="button" onClick={() => setTab('logs')} className={`px-2 py-1.5 text-sm rounded-t ${tab === 'logs' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}>
          Kuplogg
        </button>
        <button type="button" onClick={() => setTab('medicine')} className={`px-2 py-1.5 text-sm rounded-t ${tab === 'medicine' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}>
          Läkemedelslogg
        </button>
      </nav>

      {showHiveForm && canWrite('hives') && (
        <div className="border border-gray-200 rounded p-3 space-y-2 bg-gray-50">
          <h2 className="font-medium text-sm">{editingHive ? 'Redigera kupa' : 'Skapa ny kupa'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="text-xs text-gray-700">
              Kupanamn *
              <input className="mt-1 w-full" value={hiveForm.name} onChange={(e) => setHiveForm((f) => ({ ...f, name: e.target.value }))} />
            </label>
            <label className="text-xs text-gray-700">
              Bigård *
              <select className="mt-1 w-full" value={hiveForm.apiarySiteId} onChange={(e) => setHiveForm((f) => ({ ...f, apiarySiteId: e.target.value }))}>
                <option value="">Välj bigård *</option>
                {apiaries.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-gray-700">
              Kupnummer
              <input
                className="mt-1 w-full"
                type="number"
                min={1}
                placeholder={loadingNextHiveNo ? 'Hämtar nästa kupnummer...' : 'Kupnummer'}
                value={hiveForm.hiveNo}
                onChange={(e) => setHiveForm((f) => ({ ...f, hiveNo: e.target.value }))}
              />
            </label>
            <label className="text-xs text-gray-700">
              Kupa-ID (fri text)
              <input className="mt-1 w-full" value={hiveForm.hiveCode} onChange={(e) => setHiveForm((f) => ({ ...f, hiveCode: e.target.value }))} />
            </label>
            <label className="text-xs text-gray-700">
              Kuptyp
              <input className="mt-1 w-full" value={hiveForm.hiveType} onChange={(e) => setHiveForm((f) => ({ ...f, hiveType: e.target.value }))} />
            </label>
            <label className="text-xs text-gray-700">
              Kupformat
              <select className="mt-1 w-full" value={hiveForm.hiveFormatItemId} onChange={(e) => setHiveForm((f) => ({ ...f, hiveFormatItemId: e.target.value }))}>
                <option value="">Välj kupformat</option>
                {hiveFormatItems.map((i) => (
                  <option key={i.id} value={i.id}>{i.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-gray-700">
              Yngelrum
              <select className="mt-1 w-full" value={hiveForm.broodRoomTypeItemId} onChange={(e) => setHiveForm((f) => ({ ...f, broodRoomTypeItemId: e.target.value }))}>
                <option value="">Välj yngelrum</option>
                {broodRoomItems.map((i) => (
                  <option key={i.id} value={i.id}>{i.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-gray-700">
              Kupstatus
              <select className="mt-1 w-full" value={hiveForm.hiveStatusItemId} onChange={(e) => setHiveForm((f) => ({ ...f, hiveStatusItemId: e.target.value }))}>
                <option value="">Välj kupstatus</option>
                {hiveStatusItems.map((i) => (
                  <option key={i.id} value={i.id}>{i.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-gray-700">
              Drottningfärg
              <input className="mt-1 w-full" value={hiveForm.queenColor} onChange={(e) => setHiveForm((f) => ({ ...f, queenColor: e.target.value }))} />
            </label>
            <label className="text-xs text-gray-700">
              Drottningstatus
              <select className="mt-1 w-full" value={hiveForm.queenStatusItemId} onChange={(e) => setHiveForm((f) => ({ ...f, queenStatusItemId: e.target.value }))}>
                <option value="">Välj drottningstatus</option>
                {queenStatusItems.map((i) => (
                  <option key={i.id} value={i.id}>{i.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-gray-700">
              Bildat
              <input className="mt-1 w-full" type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={hiveForm.startedAt} onChange={(e) => setHiveForm((f) => ({ ...f, startedAt: e.target.value }))} />
            </label>
            <label className="text-xs text-gray-700">
              Avslutat
              <input className="mt-1 w-full" type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={hiveForm.endedAt} onChange={(e) => setHiveForm((f) => ({ ...f, endedAt: e.target.value }))} />
            </label>
            <label className="md:col-span-2 text-xs text-gray-700">
              Anteckning (kupa)
              <textarea className="mt-1 w-full" value={hiveForm.notes} onChange={(e) => setHiveForm((f) => ({ ...f, notes: e.target.value }))} />
            </label>
            <label className="md:col-span-2 text-xs text-gray-700">
              Platsanteckning (kopplad till platsobjektet i systemet)
              <textarea className="mt-1 w-full" value={hiveForm.siteNotes} onChange={(e) => setHiveForm((f) => ({ ...f, siteNotes: e.target.value }))} />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={saveHive} disabled={savingHive} className="px-2 py-1 text-sm rounded bg-success text-white hover:bg-success-hover disabled:opacity-50">
              {savingHive ? 'Sparar...' : 'Spara'}
            </button>
            <button type="button" onClick={() => { setShowHiveForm(false); setEditingHive(null); }} className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50">
              Stäng
            </button>
          </div>
        </div>
      )}

      {tab === 'hives' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-8 gap-2">
            <input className="md:col-span-3" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sök kupa/bigård/kupa-ID" />
            <select className="md:col-span-2" value={apiaryFilter} onChange={(e) => setApiaryFilter(e.target.value)}>
              <option value="">Alla bigårdar</option>
              {apiaries.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
            </select>
            <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
              Visa inaktiva
            </label>
            <button type="button" className="px-2 py-1 text-sm rounded bg-primary text-white hover:bg-primary-hover" onClick={() => { setLoading(true); loadBase(); }}>
              Filtrera
            </button>
          </div>
          <div className="overflow-x-auto rounded border border-gray-200 bg-white p-3">
            <div className="flex min-w-max flex-col gap-3 lg:flex-row lg:items-start">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="shrink-0 text-sm font-medium text-gray-700 lg:w-auto">Drottning</div>
                <div className="flex flex-nowrap gap-2">
                  {queenStatusItems.map((item) => {
                    const value = String(item.id);
                    return (
                      <button key={item.id} type="button" className={filterButton(queenStatusFilters.has(value))} onClick={() => toggleSetFilter(setQueenStatusFilters, value)}>
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="shrink-0 text-sm font-medium text-gray-700 lg:w-auto">Status</div>
                <div className="flex flex-nowrap gap-2">
                  {hiveStatusItems.map((item) => {
                    const value = String(item.id);
                    return (
                      <button key={item.id} type="button" className={filterButton(hiveStatusFilters.has(value))} onClick={() => toggleSetFilter(setHiveStatusFilters, value)}>
                        {splitStatusLabel(item.label).short}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="shrink-0 text-sm font-medium text-gray-700 lg:w-auto">Yngelrum</div>
                <div className="flex flex-nowrap gap-2">
                  {broodRoomItems.map((item) => {
                    const value = String(item.id);
                    return (
                      <button key={item.id} type="button" className={filterButton(broodRoomFilters.has(value))} onClick={() => toggleSetFilter(setBroodRoomFilters, value)}>
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="text-sm font-medium text-gray-700">
            {loading ? 'Laddar kupor...' : `${displayHives.length} ${displayHives.length === 1 ? 'kupa' : 'kupor'} matchar din sökning`}
          </div>
          {canWrite('hives') && selectedHiveCount > 0 ? (
            <div className="rounded border border-orange-200 bg-orange-50 p-3">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-semibold text-gray-900">
                  {selectedHiveCount} {selectedHiveCount === 1 ? 'kupa markerad' : 'kupor markerade'}
                </div>
                <button
                  type="button"
                  className="self-start rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 sm:self-auto"
                  onClick={() => setSelectedHiveIds(new Set())}
                  disabled={savingBulkLog}
                >
                  Avmarkera alla
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="ÅÅÅÅ-MM-DD"
                  value={bulkLogForm.logDate}
                  onChange={(e) => setBulkLogForm((f) => ({ ...f, logDate: e.target.value }))}
                  disabled={savingBulkLog}
                />
                <select
                  value={bulkLogForm.eventType}
                  onChange={(e) => setBulkLogForm((f) => ({ ...f, eventType: e.target.value, isTreatment: isTreatmentLogType(e.target.value) }))}
                  disabled={savingBulkLog}
                >
                  <option value="">Välj loggtyp *</option>
                  {hiveLogTypeItems.map((item) => (
                    <option key={item.id} value={item.label}>{item.label}</option>
                  ))}
                </select>
                {!isTreatmentLogType(selectedBulkLogType) && (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={bulkLogForm.isTreatment} onChange={(e) => setBulkLogForm((f) => ({ ...f, isTreatment: e.target.checked }))} disabled={savingBulkLog} />
                    Detta är en behandling
                  </label>
                )}
                {showBulkQueenColorField && (
                  <select value={bulkLogForm.queenColor} onChange={(e) => setBulkLogForm((f) => ({ ...f, queenColor: e.target.value }))} disabled={savingBulkLog}>
                    <option value="">Välj drottningfärg</option>
                    {['vit', 'gul', 'röd', 'grön', 'blå'].map((color) => (
                      <option key={color} value={color}>{color}</option>
                    ))}
                  </select>
                )}
                {(showBulkTreatmentFields || showBulkPreparationRemovedFields) && (
                  <>
                    <select
                      value={bulkLogForm.preparationProductId}
                      onChange={(e) => {
                        const selected = preparations.find((item) => item.id === Number(e.target.value));
                        setBulkLogForm((f) => ({
                          ...f,
                          preparationProductId: e.target.value,
                          preparation: selected?.name ?? '',
                          withdrawalDays: selected?.withdrawalDays != null ? String(selected.withdrawalDays) : f.withdrawalDays,
                        }));
                      }}
                      disabled={savingBulkLog}
                    >
                      <option value="">Välj preparat</option>
                      {preparations.map((preparation) => (
                        <option key={preparation.id} value={preparation.id}>
                          {preparation.sku ? `${preparation.sku} - ` : ''}{preparation.name}
                        </option>
                      ))}
                    </select>
                    {showBulkTreatmentFields && (
                      <input type="number" min={0} placeholder="Karenstid (dagar)" value={bulkLogForm.withdrawalDays} onChange={(e) => setBulkLogForm((f) => ({ ...f, withdrawalDays: e.target.value }))} disabled={savingBulkLog} />
                    )}
                  </>
                )}
                <textarea className="md:col-span-2" placeholder="Anteckning" value={bulkLogForm.notes} onChange={(e) => setBulkLogForm((f) => ({ ...f, notes: e.target.value }))} disabled={savingBulkLog} />
              </div>
              <div className="mt-3 flex justify-end">
                <button type="button" onClick={saveBulkLog} disabled={savingBulkLog} className="rounded bg-success px-3 py-2 text-sm text-white hover:bg-success-hover disabled:opacity-50">
                  {savingBulkLog ? 'Sparar...' : `Spara på ${selectedHiveCount} ${selectedHiveCount === 1 ? 'kupa' : 'kupor'}`}
                </button>
              </div>
            </div>
          ) : null}

          <div className="md:hidden">
            <div className="overflow-x-auto rounded border border-gray-200 bg-white">
              <table className="min-w-[780px] border-collapse text-xs">
                <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-100">
                  <tr>
                    {canWrite('hives') ? (
                      <th className="whitespace-nowrap px-2 py-2 text-left font-medium">
                        <input
                          type="checkbox"
                          checked={allVisibleHivesSelected}
                          onChange={(e) => toggleVisibleHives(e.target.checked)}
                          aria-label="Markera alla visade kupor"
                        />
                      </th>
                    ) : null}
                    <th className="whitespace-nowrap px-2 py-2 text-left font-medium">Kupa</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left font-medium">Format</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left font-medium">Yngelrum</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left font-medium">Bigård</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left font-medium">Status</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left font-medium">Drottning</th>
                    <th className="whitespace-nowrap px-2 py-2 text-right font-medium">Skattlådor</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={canWrite('hives') ? 8 : 7}>Laddar...</td></tr>
                  ) : displayHives.length === 0 ? (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={canWrite('hives') ? 8 : 7}>Inga kupor ännu.</td></tr>
                  ) : (
                    displayHives.map((h) => (
                      <tr key={h.id} className={`border-t border-gray-100 ${h.isActive ? 'bg-white' : 'bg-red-50'}`}>
                        {canWrite('hives') ? (
                          <td className="whitespace-nowrap px-2 py-2">
                            <input
                              type="checkbox"
                              checked={selectedHiveIds.has(h.id)}
                              onChange={(e) => toggleHiveSelection(h.id, e.target.checked)}
                              aria-label={`Markera ${h.hiveNo != null ? `Kupa ${h.hiveNo}` : h.name}`}
                            />
                          </td>
                        ) : null}
                        <td className={`whitespace-nowrap px-2 py-2 ${isInQuarantine(h.withdrawalUntil) ? 'bg-red-50' : ''}`}>
                          <button type="button" className="text-primary hover:underline" onClick={() => navigate(`/hives/${h.id}`)}>
                            {h.hiveNo != null ? `Kupa ${h.hiveNo}` : h.name}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">{h.hiveFormatLabel || '-'}</td>
                        <td className="whitespace-nowrap px-2 py-2">
                          <div className="inline-flex items-center gap-1">
                            <BroodRoomIcon broodRoom={h.broodRoomLabel} width={16} />
                            <span>{h.broodRoomLabel || '-'}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">{h.apiaryName || '-'}</td>
                        <td className="whitespace-nowrap px-2 py-2">{splitStatusLabel(h.hiveStatusLabel || h.hiveStatus).short}</td>
                        <td className="whitespace-nowrap px-2 py-2">
                          <div className="inline-flex items-center gap-1">
                            <QueenColorIcon color={h.queenColor} label={h.queenColor || undefined} />
                            <span>{h.queenStatusLabel || h.queenStatus || '-'}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right font-medium">{h.superBoxCount ?? 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="hidden md:block">
            <TableWrapper>
              <table className="w-full text-sm border-collapse">
                <TableHead>
                  <tr>
                    {canWrite('hives') ? (
                      <th className="text-left px-2 py-1.5 font-medium">
                        <input
                          type="checkbox"
                          checked={allVisibleHivesSelected}
                          onChange={(e) => toggleVisibleHives(e.target.checked)}
                          aria-label="Markera alla visade kupor"
                        />
                      </th>
                    ) : null}
                    <th className="text-left px-2 py-1.5 font-medium">Kupa</th>
                    <th className="text-left px-2 py-1.5 font-medium">Kupformat</th>
                    <th className="text-left px-2 py-1.5 font-medium">Yngelrum</th>
                    <th className="text-left px-2 py-1.5 font-medium">Bigård</th>
                    <th className="text-left px-2 py-1.5 font-medium">Status</th>
                    <th className="text-left px-2 py-1.5 font-medium">Drottning</th>
                    <th className="text-center px-2 py-1.5 font-medium">Karantän</th>
                    <th className="text-right px-2 py-1.5 font-medium">Skattlådor</th>
                  </tr>
                </TableHead>
                <tbody>
                  {loading ? (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={canWrite('hives') ? 9 : 8}>Laddar...</td></tr>
                  ) : displayHives.length === 0 ? (
                    <tr><td className="px-2 py-2 text-gray-500" colSpan={canWrite('hives') ? 9 : 8}>Inga kupor ännu.</td></tr>
                  ) : (
                    displayHives.map((h) => (
                      <tr key={h.id} className={`border-t border-gray-100 ${h.isActive ? 'bg-white' : 'bg-red-50'}`}>
                        {canWrite('hives') ? (
                          <td className="px-2 py-1.5">
                            <input
                              type="checkbox"
                              checked={selectedHiveIds.has(h.id)}
                              onChange={(e) => toggleHiveSelection(h.id, e.target.checked)}
                              aria-label={`Markera ${h.hiveNo != null ? `Kupa ${h.hiveNo}` : h.name}`}
                            />
                          </td>
                        ) : null}
                        <td className="px-2 py-1.5">
                          <button type="button" className="text-primary hover:underline" onClick={() => navigate(`/hives/${h.id}`)}>
                            {h.hiveNo != null ? `Kupa ${h.hiveNo}` : h.name}
                          </button>
                        </td>
                        <td className="px-2 py-1.5">{h.hiveFormatLabel || '-'}</td>
                        <td className="px-2 py-1.5">
                          <div className="inline-flex items-center gap-1">
                            <BroodRoomIcon broodRoom={h.broodRoomLabel} width={20} />
                            <span>{h.broodRoomLabel || '-'}</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5">{h.apiaryName || '-'}</td>
                        <td className="px-2 py-1.5">{splitStatusLabel(h.hiveStatusLabel || h.hiveStatus).long}</td>
                        <td className="px-2 py-1.5">
                          <div className="inline-flex items-center gap-1">
                            <QueenColorIcon color={h.queenColor} label={h.queenColor || undefined} />
                            <span>{h.queenStatusLabel || h.queenStatus || '-'}</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-center"><QuarantineIcon active={isInQuarantine(h.withdrawalUntil)} /></td>
                        <td className="px-2 py-1.5 text-right font-medium">{h.superBoxCount ?? 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableWrapper>
          </div>
        </>
      )}

      {tab === 'logs' && (
        <div className="space-y-3">
          <div className="text-sm text-gray-700">
            {selectedHive ? `Vald kupa: ${selectedHive.name}${selectedHive.apiaryName ? ` (${selectedHive.apiaryName})` : ''}` : 'Välj en kupa i kuplistan först.'}
          </div>

          {selectedHive && canWrite('hives') && (
            <div className="border border-gray-200 rounded p-3 bg-gray-50 space-y-2">
              <h2 className="font-medium text-sm">Ny kuplogg</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input type="datetime-local" value={logForm.logDate} onChange={(e) => setLogForm((f) => ({ ...f, logDate: e.target.value }))} />
                <select value={logForm.eventType} onChange={(e) => setLogForm((f) => ({ ...f, eventType: e.target.value, isTreatment: isTreatmentLogType(e.target.value), createSplitHive: createsHiveFromLogType(e.target.value) }))}>
                  <option value="">Välj loggtyp *</option>
                  {hiveLogTypeItems.map((item) => (
                    <option key={item.id} value={item.label}>{item.label}</option>
                  ))}
                </select>
                {!isTreatmentLogType(selectedLogType) && (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={logForm.isTreatment} onChange={(e) => setLogForm((f) => ({ ...f, isTreatment: e.target.checked }))} />
                    Detta är en behandling
                  </label>
                )}
                {!createsHiveFromLogType(selectedLogType) && (
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={logForm.createSplitHive} onChange={(e) => setLogForm((f) => ({ ...f, createSplitHive: e.target.checked }))} />
                    Trigger: skapa avläggare
                  </label>
                )}
                {showQueenColorField && (
                  <select value={logForm.queenColor} onChange={(e) => setLogForm((f) => ({ ...f, queenColor: e.target.value }))}>
                    <option value="">Välj drottningfärg</option>
                    {['vit', 'gul', 'röd', 'grön', 'blå'].map((color) => (
                      <option key={color} value={color}>{color}</option>
                    ))}
                  </select>
                )}
                {(showTreatmentFields || showPreparationRemovedFields) && (
                  <>
                    <select
                      value={logForm.preparationProductId}
                      onChange={(e) => {
                        const selected = preparations.find((item) => item.id === Number(e.target.value));
                        setLogForm((f) => ({
                          ...f,
                          preparationProductId: e.target.value,
                          preparation: selected?.name ?? '',
                          withdrawalDays: selected?.withdrawalDays != null ? String(selected.withdrawalDays) : f.withdrawalDays,
                        }));
                      }}
                    >
                      <option value="">Välj preparat</option>
                      {preparations.map((preparation) => (
                        <option key={preparation.id} value={preparation.id}>
                          {preparation.sku ? `${preparation.sku} - ` : ''}{preparation.name}
                        </option>
                      ))}
                    </select>
                    {showTreatmentFields && (
                      <input type="number" min={0} placeholder="Karenstid (dagar)" value={logForm.withdrawalDays} onChange={(e) => setLogForm((f) => ({ ...f, withdrawalDays: e.target.value }))} />
                    )}
                  </>
                )}
                <textarea className="md:col-span-2" placeholder="Anteckning" value={logForm.notes} onChange={(e) => setLogForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <button type="button" onClick={saveLog} disabled={savingLog} className="px-2 py-1 text-sm rounded bg-success text-white hover:bg-success-hover disabled:opacity-50">
                {savingLog ? 'Sparar...' : 'Spara logg'}
              </button>
            </div>
          )}

          <TableWrapper>
            <table className="w-full text-sm border-collapse">
              <TableHead>
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium">Datum</th>
                  <th className="text-left px-2 py-1.5 font-medium">Typ</th>
                  <th className="text-left px-2 py-1.5 font-medium">Behandling</th>
                  <th className="text-left px-2 py-1.5 font-medium">Anteckning</th>
                  <th className="text-left px-2 py-1.5 font-medium">Trigger</th>
                  {canWrite('hives') ? <th className="text-right px-2 py-1.5 font-medium">Åtgärd</th> : null}
                </tr>
              </TableHead>
              <tbody>
                {!selectedHive ? (
                  <tr><td className="px-2 py-2 text-gray-500" colSpan={canWrite('hives') ? 6 : 5}>Välj en kupa först.</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td className="px-2 py-2 text-gray-500" colSpan={canWrite('hives') ? 6 : 5}>Ingen kuplogg ännu.</td></tr>
                ) : (
                  logs.map((l) => (
                    <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-2 py-1.5">{toLocalDateTime(l.logDate)}</td>
                      <td className="px-2 py-1.5">{l.eventType}</td>
                      <td className="px-2 py-1.5">{l.isTreatment ? `${l.preparation || '-'}${l.withdrawalDays != null ? ` (${l.withdrawalDays} dagar)` : ''}` : l.eventType === 'Preparat borttaget' ? (l.preparation || '-') : '-'}</td>
                      <td className="px-2 py-1.5">{l.notes || '-'}</td>
                      <td className="px-2 py-1.5">{l.triggerCreatedHiveName || '-'}</td>
                      {canWrite('hives') ? <td className="px-2 py-1.5 text-right"><button type="button" className="rounded border border-danger px-2 py-1 text-xs text-danger hover:bg-red-50 disabled:opacity-50" onClick={() => setPendingDeleteLog(l)} disabled={deletingLogId !== null}>{deletingLogId === l.id ? 'Raderar...' : 'Radera'}</button></td> : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableWrapper>
          {canWrite('hives') && pendingDeleteLog ? <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 px-4 py-6"><div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl"><div className="text-lg font-semibold text-gray-900">Radera loggrad?</div><div className="mt-2 text-sm text-gray-600">{pendingDeleteLog.eventType} · {toLocalDateTime(pendingDeleteLog.logDate)}</div><div className="mt-1 text-sm text-gray-500">Detta går inte att ångra.</div><div className="mt-4 flex justify-end gap-2"><button type="button" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setPendingDeleteLog(null)} disabled={deletingLogId !== null}>Avbryt</button><button type="button" className="rounded-lg bg-danger px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50" onClick={() => void deleteLog(pendingDeleteLog.id)} disabled={deletingLogId !== null}>{deletingLogId === pendingDeleteLog.id ? 'Raderar...' : 'Ja, radera'}</button></div></div></div> : null}
        </div>
      )}

      {tab === 'medicine' && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={medicineFrom} onChange={(e) => setMedicineFrom(e.target.value)} />
            <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={medicineTo} onChange={(e) => setMedicineTo(e.target.value)} />
            <select value={medicineApiaryId} onChange={(e) => setMedicineApiaryId(e.target.value)}>
              <option value="">Alla bigårdar</option>
              {apiaries.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
            </select>
            <button type="button" className="px-2 py-1 text-sm rounded bg-primary text-white hover:bg-primary-hover" onClick={loadMedicine}>
              Filtrera
            </button>
          </div>

          <TableWrapper>
            <table className="w-full text-sm border-collapse">
              <TableHead>
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium">Datum</th>
                  <th className="text-left px-2 py-1.5 font-medium">Bigård</th>
                  <th className="text-left px-2 py-1.5 font-medium">Kupa</th>
                  <th className="text-left px-2 py-1.5 font-medium">Preparat</th>
                  <th className="text-left px-2 py-1.5 font-medium">Karenstid</th>
                  <th className="text-left px-2 py-1.5 font-medium">Karenstid t.o.m.</th>
                  <th className="text-left px-2 py-1.5 font-medium">Anteckning</th>
                </tr>
              </TableHead>
              <tbody>
                {medicineRows.length === 0 ? (
                  <tr><td className="px-2 py-2 text-gray-500" colSpan={7}>Ingen läkemedelslogg för valt urval.</td></tr>
                ) : (
                  medicineRows.map((r) => (
                    <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-2 py-1.5">{toLocalDateTime(r.logDate)}</td>
                      <td className="px-2 py-1.5">{r.apiaryName || '-'}</td>
                      <td className="px-2 py-1.5">{r.hiveName}</td>
                      <td className="px-2 py-1.5">{r.preparation || '-'}</td>
                      <td className="px-2 py-1.5">{r.withdrawalDays ?? '-'}</td>
                      <td className="px-2 py-1.5">{toLocalDateTime(r.withdrawalUntil)}</td>
                      <td className="px-2 py-1.5">{r.notes || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableWrapper>
        </div>
      )}
    </div>
  );
}
